import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { sendFeedback } from '../api';
import { useLang } from '../i18n';

const BugReportContext = createContext({ openBugReport: () => {} });

// Диалог «Сообщить об ошибке». openBugReport(context) — context прикладывается к письму (например, карточка занятия).
export function BugReportProvider({ children }) {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [extra, setExtra] = useState(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | error | limit
  const loggedIn = Boolean(localStorage.getItem('access_token'));

  const openBugReport = useCallback((context = null) => {
    setExtra(context);
    setMessage('');
    setState('idle');
    setOpen(true);
  }, []);

  const submit = async () => {
    setState('sending');
    try {
      await sendFeedback({
        message,
        email: loggedIn || !email.trim() ? null : email.trim(),
        page: window.location.pathname + window.location.search,
        context: { lang, screen: `${window.innerWidth}x${window.innerHeight}`, ...(extra || {}) },
      });
      setState('sent');
    } catch (err) {
      setState(err.response?.status === 429 ? 'limit' : 'error');
    }
  };

  const value = useMemo(() => ({ openBugReport }), [openBugReport]);
  return (
    <BugReportContext.Provider value={value}>
      {children}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('bug.title')}</DialogTitle>
        <DialogContent>
          {state === 'sent' ? (
            <Alert severity="success">{t('bug.thanks')}</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {extra?.card ? t('bug.cardHint') : t('bug.hint')}
              </Typography>
              <TextField autoFocus fullWidth multiline minRows={4} value={message}
                onChange={(e) => setMessage(e.target.value)} placeholder={t('bug.placeholder')}
                inputProps={{ maxLength: 5000 }} />
              {!loggedIn && (
                <TextField fullWidth type="email" label={t('bug.email')} value={email}
                  onChange={(e) => setEmail(e.target.value)} sx={{ mt: 2 }} />
              )}
              {state === 'error' && <Alert severity="error" sx={{ mt: 2 }}>{t('bug.error')}</Alert>}
              {state === 'limit' && <Alert severity="warning" sx={{ mt: 2 }}>{t('bug.limit')}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{state === 'sent' ? t('bug.close') : t('bug.cancel')}</Button>
          {state !== 'sent' && (
            <Button variant="contained" onClick={submit} disabled={state === 'sending' || message.trim().length < 3}>
              {t('bug.send')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </BugReportContext.Provider>
  );
}

export const useBugReport = () => useContext(BugReportContext);
